import { Box, Flex, Header } from 'components';
import { useState } from 'react';
import { FrameEmail, FrameEmailCode, FramePhone, FramePhoneCode } from './components';
import { ModalLoader } from 'shared/components';
import { AuthError, ModalError } from '../modal-error';
import { ApiAuth } from 'common/api';
import { ApiError, ApiErrorCode } from '@bustickus/api';
import { RoutingUrls } from 'common/const';
import { phoneClear, phoneFormat } from 'pages/page-auth/helpers';
import { AuthService } from 'common/services';

type Stage = 'email' | 'email_code' | 'phone' | 'phone' | 'phone_code' | 'code_not_found';

function FrameRegister() {
  const [stage, setStage] = useState<Stage>('email');
  const [sessionId, setSessionId] = useState<string>('');
  const [codeSendedAt, setCodeSendedAt] = useState<Date>(new Date());
  const [source, setSource] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<AuthError | null>(null);

  const handleEmailCodeResend = async () => {
    try {
      setLoading(true);
      const api = ApiAuth.getInstance();
      await api.resendCodeEmail(sessionId);
      setCodeSendedAt(new Date());
      setStage('email_code');
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === ApiErrorCode.NotFound) {
          setAuthError('session_not_found');
        } else {
          setAuthError('unexpected');
        }
      } else {
        setAuthError('unexpected');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneCodeResend = async () => {
    try {
      setLoading(true);
      const api = ApiAuth.getInstance();
      await api.resendCodePhone(sessionId);
      setCodeSendedAt(new Date());
      setStage('phone_code');
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === ApiErrorCode.NotFound) {
          setAuthError('session_not_found');
        } else {
          setAuthError('unexpected');
        }
      } else {
        setAuthError('unexpected');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterClick = async (email: string) => {
    try {
      setLoading(true);
      const api = ApiAuth.getInstance();
      const response = await api.registerEmail(email);
      setCodeSendedAt(new Date());
      setSessionId(response.sessionId);
      setSource(response.source);
      setStage('email_code');
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === ApiErrorCode.Conflict) {
          setAuthError('already_exists');
        } else if (e.code === ApiErrorCode.BadRequest) {
          setAuthError('invalid_data');
        } else {
          setAuthError('unexpected');
        }
      } else {
        setAuthError('unexpected');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailCodeConfirm = async (code: string) => {
    try {
      setLoading(true);
      const api = ApiAuth.getInstance();
      await api.registerEmailCode(sessionId, code);
      setStage('phone');
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === ApiErrorCode.NotFound) {
          setAuthError('code_not_found');
        } else {
          setAuthError('unexpected');
        }
      } else {
        setAuthError('unexpected');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneRegisterClick = async (phone: string) => {
    try {
      setLoading(true);
      const api = ApiAuth.getInstance();
      await api.registerPhone(sessionId, phoneClear(phone));
      setSource(phoneFormat(phone));
      setCodeSendedAt(new Date());
      setStage('phone_code');
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === ApiErrorCode.Conflict) {
          setAuthError('already_exists');
        } else if (e.code === ApiErrorCode.BadRequest) {
          setAuthError('invalid_data');
        } else {
          setAuthError('unexpected');
        }
      } else {
        setAuthError('unexpected');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneCodeConfirm = async (code: string) => {
    try {
      setLoading(true);
      const api = ApiAuth.getInstance();
      const response = await api.registerPhoneCode(sessionId, code);
      AuthService.getInstance().set(response.token);
      window.location.href = RoutingUrls.Main;
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === ApiErrorCode.NotFound) {
          setAuthError('code_not_found');
        } else if (e.code === ApiErrorCode.BadRequest) {
          setAuthError('invalid_data');
        } else if (e.code === ApiErrorCode.Conflict) {
          setAuthError('already_exists');
        } else {
          setAuthError('unexpected');
        }
      } else {
        setAuthError('unexpected');
      }
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <ModalLoader />}
      {authError != null && (
        <ModalError
          error={authError}
          onClose={() => setAuthError(null)}
        />
      )}
      <Box padding={20}>
        <Flex
          direction='column'
          gap={20}
        >
          <Header
            align='center'
            size='bigger'
          >
            Регистрация
          </Header>

          {stage === 'email' && <FrameEmail onRegisterClick={handleRegisterClick} />}
          {stage === 'email_code' && (
            <FrameEmailCode
              source={source}
              sendedAt={codeSendedAt}
              onContinueClick={handleEmailCodeConfirm}
              onSendAgainClick={handlePhoneCodeResend}
              onBackClick={() => setStage('email')}
            />
          )}
          {stage === 'phone' && <FramePhone onRegisterClick={handlePhoneRegisterClick} />}
          {stage === 'phone_code' && (
            <FramePhoneCode
              source={source}
              sendedAt={codeSendedAt}
              onContinueClick={handlePhoneCodeConfirm}
              onSendAgainClick={handlePhoneCodeResend}
              onBackClick={() => setStage('phone')}
            />
          )}
        </Flex>
      </Box>
    </div>
  );
}

export default FrameRegister;
